import { loadStatus } from '../entity/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as manApi from 'api/ManApi';
import { RootState, TThunkApi } from 'store/store';
import { ITagView } from '../providers/providerTypes';

export interface ITemplateAdmin {
  userId: number;
  templateId: number;
}
interface ISurveyInfo {
  fillToken?: string;
  editToken?: string;
  name?: string;
  id?: number;
}
export interface IProviderTemplate {
  id: number;
  appInstanceId: number;
  providerInfo?: ISurveyInfo;
  name: string;
  description?: string;
  tag?: ITagView;
  categoryTag?: ITagView;
  admins?: Array<ITemplateAdmin>;
  deleted?: boolean;
  askLocation: boolean;
  askProfilePhoto: boolean;
  askSocialInfo: boolean;
  askSelfDocuments: boolean;
  askRelatedDocs: boolean;
  single: boolean;
  required: boolean;
}

export interface IProviderTemplates {
  data: IProviderTemplate;
  state: loadStatus;
}

const initialState: IProviderTemplates = {
  data: {
    id: 0,
    appInstanceId: 0,
    name: '',
    askLocation: false,
    askProfilePhoto: false,
    askSocialInfo: false,
    askSelfDocuments: false,
    askRelatedDocs: false,
    single: false,
    required: false,
  },
  state: 'notLoaded',
};

export interface ITemplateReportMark {
  completedTaskCountMain: boolean;
  inProgressTaskCountMain: boolean;
  notStartedTaskCountMain: boolean;
  averageProgressMain: boolean;
  completedTaskCountSecondary: boolean;
  inProgressTaskCountSecondary: boolean;
  notStartedTaskCountSecondary: boolean;
  averageProgressSecondary: boolean;
  completedTaskCountVerifier: boolean;
  rejectedTaskCountVerifier: boolean;
  notStartedTaskCountVerifier: boolean;
  completedAssignmentCount: boolean;
  inProgressAssignmentCount: boolean;
  notStartedAssignmentCount: boolean;
  averageAssignmentProgress: boolean;
}

export interface ITemplateReportValues {
  completedTaskCountMain?: number;
  inProgressTaskCountMain?: number;
  notStartedTaskCountMain?: number;
  averageProgressMain?: number;
  completedTaskCountSecondary?: number;
  inProgressTaskCountSecondary?: number;
  notStartedTaskCountSecondary?: number;
  averageProgressSecondary?: number;
  completedTaskCountVerifier?: number;
  rejectedTaskCountVerifier?: number;
  notStartedTaskCountVerifier?: number;
  completedAssignmentCount?: number;
  inProgressAssignmentCount?: number;
  notStartedAssignmentCount?: number;
  averageAssignmentProgress?: number;
  startMonthIndex: number;
  fieldValue?: string;
  title?: string;
  endMonthIndex: number;
}

const templateManipulate = (template: IProviderTemplate) => {
  const newTemplate: any = { ...template };
  newTemplate.admins = template.admins!.reduce((adminsMap, admin) => {
    return (adminsMap[admin.userId] = admin);
  }, {});
  return newTemplate;
};

function setProviderTemplateHandler(
  state: IProviderTemplates,
  action: PayloadAction<{ template: IProviderTemplate }>
) {
  state.data[action.payload.template.id] = templateManipulate(
    action.payload.template
  );
}

function setSeveralTemplatesHandler(
  state: IProviderTemplates,
  action: PayloadAction<{ templates: Array<IProviderTemplate> }>
) {
  action.payload.templates.forEach((template) => {
    state.data[template.id] = templateManipulate(template);
  });
  state.state = 'loaded';
}

function addAdminHandler(
  state: IProviderTemplates,
  action: PayloadAction<{ adminView: ITemplateAdmin }>
) {
  if (!state.data[action.payload.adminView.templateId]) {
    state.data = {
      ...state.data,
      [action.payload.adminView.templateId]: {
        admins: {},
      },
    };
  }

  if (
    !state.data[action.payload.adminView.templateId].admins[
      action.payload.adminView.userId
    ]
  ) {
    state.data[action.payload.adminView.templateId].admins = {
      ...state.data[action.payload.adminView.templateId].admins,
      [action.payload.adminView.userId]: {},
    };
  }

  state.data[action.payload.adminView.templateId].admins[
    action.payload.adminView.userId
  ] = action.payload.adminView;
}

function removeAdminHandler(
  state: IProviderTemplates,
  action: PayloadAction<{ adminView: ITemplateAdmin }>
) {
  delete state.data[action.payload.adminView.templateId].admins[
    action.payload.adminView.userId
  ];
}

export const saveProviderTemplateFn = async (
  {
    template,
    isUpdate = false,
  }: { template: IProviderTemplate; isUpdate: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (isUpdate) {
    return manApi
      .updateProviderTemplate(store, template)
      .then(({ data }) => {
        ThunkApi.dispatch(setProviderTemplate({ template: data }));
        return Promise.resolve({ templateId: data.id as number | string });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }

  return manApi
    .addProviderTemplate(store, template)
    .then(({ data }) => {
      ThunkApi.dispatch(setProviderTemplate({ template: data }));
      return Promise.resolve({ templateId: data.id as number | string });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const saveProviderTemplate = createAsyncThunk(
  'templates/saveProviderTemplate',
  saveProviderTemplateFn
);

export const requireAllTemplatesFn = async (
  { appInstanceId }: { appInstanceId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (store.templates.state === 'loaded') {
    return Promise.resolve(null);
  }

  return manApi
    .getAllProviderTemplates(store, appInstanceId)
    .then(({ data }) => {
      if (data) {
        ThunkApi.dispatch(setSeveralTemplates({ templates: data }));
      }
      return Promise.resolve({ templates: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireAllTemplates = createAsyncThunk(
  'templates/requireAllTemplates',
  requireAllTemplatesFn
);

export const requireTemplateFn = async (
  { templateId }: { templateId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const template = store.templates.data[Number(templateId)];

  if (template) {
    ThunkApi.dispatch(setProviderTemplate({ template }));
    return Promise.resolve({ template });
  }

  return manApi
    .getProviderTemplate(store, templateId)
    .then(({ data }) => {
      ThunkApi.dispatch(setProviderTemplate({ template: data }));
      return Promise.resolve({ template: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireTemplate = createAsyncThunk(
  'templates/requireTemplate',
  requireTemplateFn
);

export const changeTemplateAdminFn = async (
  {
    adminView,
    isRemoveAction = false,
  }: { adminView: ITemplateAdmin; isRemoveAction: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (isRemoveAction) {
    return manApi
      .removeAdminFromTemplate(store, adminView)
      .then(({ data }) => {
        ThunkApi.dispatch(removeAdmin({ adminView: data }));
        ThunkApi.dispatch(addAdmin({ adminView: data }));
        return Promise.resolve({ adminView: data });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }

  return manApi
    .addAdminToTemplate(store, adminView)
    .then(({ data }) => {
      ThunkApi.dispatch(addAdmin({ adminView: data }));
      return Promise.resolve({ adminView: data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const changeTemplateAdmin = createAsyncThunk(
  'templates/changeTemplateAdmin',
  changeTemplateAdminFn
);

const templatesSlice = createSlice({
  name: 'templates',
  initialState: initialState,
  reducers: {
    setProviderTemplate: setProviderTemplateHandler,
    setSeveralTemplates: setSeveralTemplatesHandler,
    addAdmin: addAdminHandler,
    removeAdmin: removeAdminHandler,
  },
});

export const {
  setProviderTemplate,
  setSeveralTemplates,
  addAdmin,
  removeAdmin,
} = templatesSlice.actions;

export {
  saveProviderTemplate,
  requireAllTemplates,
  requireTemplate,
  changeTemplateAdmin,
};

export default templatesSlice.reducer;
